import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  UserGroupIcon,
  LightBulbIcon,
  TrophyIcon,
  HeartIcon,
  ArrowRightIcon,
  CheckIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  useSEO(pageConfigs.about);

  const handleGetStarted = () => {
    navigate('/quote');
  };

  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };

  const handleViewServices = () => {
    navigate('/');
  };

  const handleCallPhone = () => {
    window.location.href = 'tel:+919711834001';
  };

  const handleSendEmail = () => {
    window.location.href = 'mailto:info@3arrowsmedia.com';
  };

  const values = [
    {
      title: "Innovation",
      description: "We stay ahead of digital trends and continuously evolve our strategies to deliver cutting-edge solutions.",
      icon: <LightBulbIcon className="w-8 h-8" />
    },
    {
      title: "Results-Driven",
      description: "Every campaign is designed with measurable outcomes in mind, ensuring maximum ROI for our clients.",
      icon: <TrophyIcon className="w-8 h-8" />
    },
    {
      title: "Client-Centric",
      description: "Your success is our success. We build lasting partnerships based on trust, transparency, and exceptional service.",
      icon: <HeartIcon className="w-8 h-8" />
    },
    {
      title: "Expertise",
      description: "Our team combines years of experience with the latest industry knowledge to deliver superior results.",
      icon: <UserGroupIcon className="w-8 h-8" />
    }
  ];

  const team = [
    {
      name: "Sarah Johnson",
      role: "CEO & Founder",
      bio: "15+ years in digital marketing with expertise in lead generation and growth strategies.",
      image: "/api/placeholder/300/300"
    },
    {
      name: "Michael Chen",
      role: "Head of Development",
      bio: "Full-stack developer specializing in mobile and web applications with 12+ years experience.",
      image: "/api/placeholder/300/300"
    },
    {
      name: "Emily Rodriguez",
      role: "Marketing Director",
      bio: "Digital marketing strategist with proven track record in PPC, SEO, and social media campaigns.",
      image: "/api/placeholder/300/300"
    },
    {
      name: "David Thompson",
      role: "Creative Director",
      bio: "Award-winning designer with expertise in brand identity and user experience design.",
      image: "/api/placeholder/300/300"
    }
  ];

  const stats = [
    { number: "500+", label: "Clients Served", description: "Businesses transformed" },
    { number: "98%", label: "Client Retention", description: "Long-term partnerships" },
    { number: "$50M+", label: "Revenue Generated", description: "For our clients" },
    { number: "8+", label: "Years Experience", description: "In digital marketing" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.about} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto" >
            
            <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
              Driving Digital Success
              <span className="text-blue-600"> Since 2016</span>
            </h1>
            
            <p className="text-lg text-slate-700 mb-8 leading-relaxed">
              We're a passionate team of digital marketing experts, developers, and strategists 
              dedicated to helping businesses thrive in the digital landscape. Our mission is to 
              deliver measurable results that drive growth and exceed expectations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleGetStarted}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <span>Get Started</span>
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-slate-900 mb-2">{stat.number}</div>
                <div className="text-lg font-semibold text-slate-700 mb-1">{stat.label}</div>
                <div className="text-sm text-slate-500">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-6 text-slate-600">
                <p>
                  Founded in 2016, 3 Arrows Media began with a simple vision: to help businesses 
                  navigate the complex world of digital marketing with clarity, strategy, and 
                  measurable results.
                </p>
                <p>
                  What started as a small team of passionate marketers has grown into a full-service 
                  digital agency, serving clients across various industries. We've helped startups 
                  launch their first campaigns, assisted established businesses in digital transformation, 
                  and supported enterprises in scaling their online presence.
                </p>
                <p>
                  Our name "3 Arrows" represents our three core focus areas: Strategy, Execution, 
                  and Results. Every project we undertake is guided by these principles, ensuring 
                  that our clients not only meet their goals but exceed them.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-display font-bold text-slate-900 mb-6">Why Choose Us?</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckIcon className="w-5 h-5 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Proven Track Record</h4>
                    <p className="text-slate-600 text-sm">500+ successful campaigns and counting</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckIcon className="w-5 h-5 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Full-Service Solutions</h4>
                    <p className="text-slate-600 text-sm">From strategy to execution, we handle it all</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckIcon className="w-5 h-5 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Transparent Reporting</h4>
                    <p className="text-slate-600 text-sm">Clear metrics and regular performance updates</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckIcon className="w-5 h-5 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Dedicated Support</h4>
                    <p className="text-slate-600 text-sm">Your success is our priority</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{value.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help your business achieve its digital marketing goals.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleCallPhone}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <PhoneIcon className="w-5 h-5" />
              <span>Call (+91) 9711834001</span>
            </button>
            <button 
              onClick={handleSendEmail}
              className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <EnvelopeIcon className="w-5 h-5" />
              <span>Send Email</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;